import { useLocation, useNavigate, useSearch } from '@tanstack/react-router';
import { type ReactNode, useEffect } from 'react';
import { useSignals } from '../../lib/signals.hooks.js';
import { SIGNALS_SECTION_ID } from '../../lib/signals-search.js';
import { useRunResult } from '../../lib/workflows.hooks.js';
import { OutcomeDrawer } from '../workflows/OutcomeDrawer.js';
import { SignalsSection } from './SignalsSection.js';

// The only Signals list (Figma 11772:77162), a section of HOME. Its tab, grouping and open result
// live in the dashboard's search, so changing them must not scroll HOME back to the top.
export function HomeSignals(): ReactNode {
  const query = useSignals();
  const search = useSearch({ from: '/dashboard/' });
  const navigate = useNavigate({ from: '/dashboard/' });
  const hash = useLocation({ select: (location) => location.hash });
  const runId = search.signalRun ?? null;
  const listed = query.signals.find((signal) => signal.runId === runId) ?? null;
  // A link to a run outside the feed still names the drawer from the run's own result.
  const result = useRunResult(runId);
  const { markRead } = query;
  const listedRunId = listed?.runId ?? null;

  // A shared link counts as opening the signal, once it is one the feed lists.
  useEffect(() => {
    if (listedRunId !== null) markRead(listedRunId);
  }, [listedRunId, markRead]);

  // "View all/dismissed signals" from the panel lands here with the #signals hash.
  useEffect(() => {
    if (hash !== SIGNALS_SECTION_ID) return;
    const section = document.getElementById(SIGNALS_SECTION_ID);
    section?.scrollIntoView({ block: 'start' });
    section?.querySelector<HTMLElement>('#signals-section-title')?.focus({ preventScroll: true });
  }, [hash]);

  return (
    <>
      <SignalsSection
        query={query}
        tab={search.signalsTab ?? 'active'}
        grouping={search.signalsGroup ?? 'none'}
        onTabChange={(signalsTab) =>
          void navigate({
            replace: true,
            resetScroll: false,
            search: (previous) => ({ ...previous, signalsTab }),
          })
        }
        onGroupingChange={(signalsGroup) =>
          void navigate({
            replace: true,
            resetScroll: false,
            search: (previous) => ({ ...previous, signalsGroup }),
          })
        }
      />
      <OutcomeDrawer
        outcome={
          runId === null
            ? null
            : {
                runId,
                name: listed?.workflow ?? result.data?.actionName ?? 'Signal',
                kind: 'report',
                date: null,
              }
        }
        scope={
          listed === null || listed.startDate === ''
            ? null
            : {
                startDate: listed.startDate,
                endDate: listed.endDate,
                accountIds: listed.accountIds,
              }
        }
        leadingKpi={listed?.kpi ?? ''}
        onClose={() =>
          void navigate({
            replace: true,
            resetScroll: false,
            search: (previous) => ({ ...previous, signalRun: undefined }),
          })
        }
      />
    </>
  );
}
